import { generateId } from "./utils";

export const handleAddOption = (event, options, setOptions, setEditModes) => {
  event.preventDefault();
  const newOption = {
    id: generateId(),
    title: "",
    values: [{ id: generateId(), name: "", sku: "", image: null }],
    order: options.length
  };
  
  setOptions(prevOptions => [...prevOptions, newOption]);
  setEditModes(prev => ({
    ...prev,
    [newOption.id]: true
  }));
};

export const handleInitialAddOption = (e, setShowOptions, options, setOptions, setEditModes) => {
  setShowOptions(true);
  handleAddOption(e, options, setOptions, setEditModes);
};

export const handleOptionNameChange = (optionId, newName, setOptions) => {
  setOptions((prevOptions) =>
    prevOptions.map((option) =>
      option.id === optionId ? { ...option, title: newName } : option
    )
  );
};

export const handleOptionValueChange = (optionId, valueId, field, value, setOptions) => {
  setOptions((prevOptions) => {
    const newOptions = prevOptions.map((option) =>
      option.id === optionId
        ? {
            ...option,
            values: option.values.map((val) =>
              val.id === valueId ? { ...val, [field]: value } : val
            ),
          }
        : option
    );

    const option = newOptions.find((option) => option.id === optionId);
    const valueIndex = option.values.findIndex((val) => val.id === valueId);

    // Only add new empty value if the current value is being filled and it's the last one
    if (field === 'name' && valueIndex === option.values.length - 1 && value.trim() !== '') {
      option.values.push({ id: generateId(), name: '', sku: '', image: null });
    } 

    // Cleanup empty values: if there are multiple completely empty values, keep only one.
    const completelyEmptyValues = option.values.filter(v => 
      v.name.trim() === '' && (!v.sku || v.sku.trim() === '')
    );
    const nonEmptyValues = option.values.filter(v => 
      v.name.trim() !== '' || (v.sku && v.sku.trim() !== '')
    );
    
    if (completelyEmptyValues.length > 1) {
      option.values = [...nonEmptyValues, completelyEmptyValues[0]];
    } else if (completelyEmptyValues.length === 1 && nonEmptyValues.length === 0 && option.values.length > 1) {
      option.values = [completelyEmptyValues[0]];
    }

    return newOptions;
  });
};

export const handleDeleteOption = (id, setOptions, setEditModes) => {
  setOptions((prevOptions) => prevOptions.filter((option) => option.id !== id));
  setEditModes((prev) => {
    const newModes = { ...prev };
    delete newModes[id];
    return newModes;
  });
};

export const handleDeleteValue = (optionId, valueId, setOptions) => {
  setOptions((prevOptions) =>
    prevOptions.map((option) =>
      option.id === optionId
        ? {
            ...option,
            values: option.values.filter((val) => val.id !== valueId),
          }
        : option
    )
  );
};

export const handleToggleValueDisabled = (optionId, valueId, setOptions, setVariants) => {
  // The function ensures variant availability stays consistent 
  // with their dimension values' availability.

  setOptions((prevOptions) => {
    const newOptions = prevOptions.map((option) =>
      option.id === optionId
        ? {
            ...option,
            values: option.values.map((val) =>
              val.id === valueId ? { ...val, disabled: !val.disabled } : val
            ),
          }
        : option
    );

    // Find the toggled value to determine its new disabled state
    const toggledOption = newOptions.find(option => option.id === optionId);
    const toggledValue = toggledOption.values.find(val => val.id === valueId);
    const isNowDisabled = toggledValue.disabled;

    // Update corresponding variants
    if (setVariants) {
      setVariants((prevVariants) =>
        prevVariants.map((variant) => {
          // Check if this variant uses the toggled dimension value
          const usesToggledValue = variant.valueIds && variant.valueIds.includes(valueId);

          if (usesToggledValue) {
            return { ...variant, enabled: !isNowDisabled };
          }

          // For variants that don't use the toggled value, check if all their dimension values are enabled
          if (!isNowDisabled && variant.valueIds) { // Only re-enable variants when we're enabling a dimension value
            const allValuesEnabled = variant.valueIds.every(vId => {
              const option = newOptions.find(opt => opt.values.some(val => val.id === vId));
              const value = option?.values.find(val => val.id === vId);
              return value && !value.disabled;
            });
            
            if (allValuesEnabled) {
              return { ...variant, enabled: true };
            }
          }

          return variant;
        })
      );
    }

    return newOptions;
  });
};

export const handleEdit = (optionId, setEditModes) => {  
  setEditModes(prev => ({
    ...prev,
    [optionId]: true
  }));

};

export const handleDone = (optionId, setEditModes) => {
  setEditModes(prev => ({
    ...prev,
    [optionId]: false
  }));
};

export const updateVariant = (event, variantId, field, value, setVariants) => {
  event.preventDefault();
  
  // Don't try to convert image URLs to numbers
  const parsedValue = field === 'image' 
    ? value 
    : (value === '' ? '' : (isNaN(value) ? value : Number(value)));
    
  setVariants((prevVariants) =>
    prevVariants.map((variant) =>
      variant.id === variantId 
        ? { ...variant, [field]: parsedValue }
        : variant
    )
  );
};

export const handleImageUpload = async (e, callback, ...args) => {
  e.preventDefault();
  const file = e.target.files[0];
  const imageUploadUrl = gon.upload_image_url

  const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

  if (file) {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(imageUploadUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      const url = data.file;
      callback(e, url, ...args);
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  }
};

export const handleVariantImageUpload = (e, url, variant, setVariants) => {
  updateVariant(e, variant.id, "image", url, setVariants)
};

export const handleRemoveVariantImage = (e, variant, setVariants) => {
  if (e) e.preventDefault();
  updateVariant(e || { preventDefault: () => {} }, variant.id, "image", null, setVariants);
};

export const handleRemoveGroupImage = (e, option, optionValue, setOptions, groupedVariants, setVariants) => {
  if (e) e.preventDefault();
  
  // Remove image from option value
  handleOptionValueChange(option.id, optionValue.id, "image", null, setOptions);
  
  // Remove image only from enabled variants in this group
  groupedVariants
    .filter(variant => variant.enabled)
    .forEach(variant => 
      updateVariant(e || { preventDefault: () => {} }, variant.id, "image", null, setVariants)
    );
};